import { Add, Delete, Edit } from "@mui/icons-material";
import { Box, Button } from "@mui/material";
import { DataGridPremium, GridCellParams, GridColDef } from "@mui/x-data-grid-premium";
import {
	Contact,
	DunningLevel,
	useDeleteDunningLevelMutation,
	useGetAllDunningLevelsLazyQuery,
} from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { useEffect, useMemo, useRef, useState } from "react";

import ConfirmationDialog, {
	ConfirmationDialogHandle,
} from "@repo/legacy/dialogs/ConfirmationForwardRefDialog";
import { DialogDunningLevel, DunningLevelDialogHandle } from "../dialogs/DialogDunningLevel";
import { formatCurrencyNumber } from "../../helper/Index";

export default function Dunnings(
	props: Readonly<{
		contact: Contact | null;
	}>,
) {
	const { t } = useTranslation();
	const { showNotification } = useNotification();

	const [dunningLevels, setDunningLevels] = useState<DunningLevel[]>([]);

	const dialogRef = useRef<DunningLevelDialogHandle>(null);
	const confirmDialogRef = useRef<ConfirmationDialogHandle>(null);

	const [getAllDunningLevels] = useGetAllDunningLevelsLazyQuery({
		variables: { cotractorId: props.contact?.id ?? "", name: "", onlyActive: true },
		fetchPolicy: "no-cache",
		onCompleted(data) {
			if (data?.allDunningLevels) {
				setDunningLevels(data.allDunningLevels as DunningLevel[]);
			}
		},
	});

	const [deleteDunningLevel] = useDeleteDunningLevelMutation({
		onCompleted(data) {
			if (data.deleteDunningLevel.boolean) {
				getAllDunningLevels();
				showNotification(t("contractors.dunning.deleted"), "success");
			}
			data.deleteDunningLevel.errors?.forEach((error) => {
				if ("message" in error) showNotification(error.message, "error");
			});
		},
		onError(error) {
			showNotification(error.message, "error");
		},
	});

	useEffect(() => {
		if (props.contact?.id) {
			getAllDunningLevels();
		}
	}, [props.contact?.id]);

	const columns: GridColDef<DunningLevel>[] = useMemo(
		() => [
			{
				field: "level",
				headerName: t("level"),
				width: 80,
			},
			{
				field: "name",
				headerName: t("name"),
				flex: 1,
			},
			{
				field: "paymentDays",
				headerName: t("payment-days"),
				width: 140,
			},
			{
				field: "feeAmount",
				headerName: t("fee-amount"),
				width: 200,
				renderCell: (params: GridCellParams) => formatCurrencyNumber(params.row.feeAmount),
			},
			{
				field: "actions",
				headerName: t("actions"),
				headerAlign: "center",
				sortable: false,
				width: 120,
				renderCell: (params: GridCellParams) => (
					<Box
						sx={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							gap: 0,
						}}>
						<Button
							data-testid="edit-dunning-button"
							onClick={() => dialogRef.current?.show(params.row as DunningLevel)}>
							<Edit />
						</Button>
						<Button
							data-testid="delete-dunning-button"
							onClick={() =>
								confirmDialogRef.current?.show({
									text: t("contractors.dunning.delete-message"),
									onYes: () => deleteDunningLevel({ variables: { id: params.row.id as string } }),
								})
							}>
							<Delete />
						</Button>
					</Box>
				),
			},
		],
		[t],
	);

	return (
		<Box sx={{ height: "100%", width: "100%" }}>
			<Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
				<Button
					data-testid="add-dunning-level-button"
					variant="contained"
					className="greenButton"
					onClick={() => dialogRef.current?.show(null)}
					startIcon={<Add />}>
					{t("add-new-dunning-level")}
				</Button>
			</Box>
			<DataGridPremium
				getRowId={(row: DunningLevel) => row.id}
				rows={dunningLevels}
				columns={columns}
				disableRowSelectionOnClick
				className="table"
				hideFooter={true}
			/>
			<DialogDunningLevel
				ref={dialogRef}
				level={dunningLevels.length + 1}
				contractorId={props.contact?.id ?? ""}
				onSaved={getAllDunningLevels}
			/>
			<ConfirmationDialog ref={confirmDialogRef} />
		</Box>
	);
}
